import { AlertTriangle } from "lucide-react";
import { formatAgeRange } from "@/constants/plannerCategories";

// "Full" and "Under Ratio" are SEPARATE, independent states — a room can be full AND
// understaffed at the same time, so both badges must be able to render together rather than
// one hiding the other.
const BADGE_STYLE = {
  full: "bg-[#C97B7B]/10 text-[#B85C5C] border-[#C97B7B]/30",
  under: "bg-[#D8A657]/10 text-[#B8873C] border-[#D8A657]/30",
  ok: "bg-[#5B9E7A]/10 text-[#3F7A5C] border-[#5B9E7A]/30",
};
const BADGE_DOT = { full: "bg-[#C97B7B]", under: "bg-[#D8A657]", ok: "bg-[#5B9E7A]" };

export const LiveRatiosPanel = ({ categoryGroups, unassignedChildren, staffMissingRoomAssignment = [], sharedPools = [] }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="live-ratios-panel">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-600">Room Ratios — Live</h2>
      </div>

      {categoryGroups.length === 0 && <p className="text-sm text-slate-300">No rooms set up yet.</p>}

      <div className="space-y-4">
        {categoryGroups.map((g) => {
          const groupBadges = [];
          if (g.isFull) groupBadges.push("full");
          if (g.isUnderRatio) groupBadges.push("under");
          if (groupBadges.length === 0) groupBadges.push("ok");
          return (
            <div key={g.category} className="rounded-xl border border-slate-200 p-3" data-testid={`category-group-${g.category}`}>
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-800">
                    {g.categoryLabel}
                    {g.rooms.length > 0 && g.rooms[0].minAgeMonths !== undefined && g.rooms[0].minAgeMonths !== null ? (
                      <span className="text-slate-400 font-normal"> · {formatAgeRange(g.rooms[0].minAgeMonths, g.rooms[0].maxAgeMonths, g.category)}</span>
                    ) : null}
                    {g.rooms.length > 0 && g.rooms[0].effectiveRatio ? (
                      <span className="text-slate-400 font-normal"> — Staff Ratio 1:{g.rooms[0].effectiveRatio}</span>
                    ) : null}
                  </span>
                </div>
                <div className="flex flex-col items-end gap-1" data-testid={`category-badges-${g.category}`}>
                  <span className="flex items-center gap-1 flex-wrap justify-end">
                    {groupBadges.map((status) => (
                      <span
                        key={status}
                        className={`flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full border ${BADGE_STYLE[status]}`}
                        data-testid={`category-badge-${status}-${g.category}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${BADGE_DOT[status]}`} />
                        {status === "full" ? "Full" : status === "under" ? "Under Ratio" : "Ratio OK"}
                      </span>
                    ))}
                  </span>
                  {g.rooms.length > 1 && (
                    <span className="text-[10px] text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5" data-testid={`category-room-count-${g.category}`}>
                      {g.rooms.length} rooms
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-slate-500 mb-3 flex-wrap" data-testid={`category-totals-${g.category}`}>
                <span>Children: <strong className="text-slate-700">{g.totalChildren}</strong></span>
                <span data-testid={`category-capacity-${g.category}`}>
                  Capacity: <strong className="text-slate-700">{g.totalCapacity}</strong>
                  <span className="text-slate-400"> (needs {g.totalStaffNeededAtCapacity} staff at full)</span>
                </span>
                <span>Open Spots: <strong className="text-slate-700">{g.totalOpenSpots}</strong></span>
                <span data-testid={`category-open-slots-with-staff-${g.category}`}>
                  Open Slots with Current Staff: <strong className={g.totalOpenSlotsWithStaff === 0 ? "text-[#B85C5C]" : "text-slate-700"}>{g.totalOpenSlotsWithStaff}</strong>
                </span>
                {g.overflow > 0 && (
                  <span
                    className="flex items-center gap-1 text-[#B85C5C] font-semibold"
                    title="These children exceed the combined capacity of this category's rooms."
                    data-testid={`category-overflow-${g.category}`}
                  >
                    <AlertTriangle className="w-3 h-3" /> {g.overflow} over capacity
                  </span>
                )}
              </div>

              <div className="flex gap-3 overflow-x-auto pb-1">
                {g.rooms.map((r) => {
                  const badges = [];
                  if (r.isFull) badges.push("full");
                  if (r.isUnderRatio) badges.push("under");
                  if (badges.length === 0) badges.push("ok");
                  const pct = r.capacity > 0 ? Math.min(100, (r.childrenPresent / r.capacity) * 100) : 0;
                  return (
                    <div key={r.roomId} className="rounded-xl border border-slate-200 p-3 flex-1 min-w-[260px]" data-testid={`ratio-room-card-${r.roomId}`}>
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                          {r.roomName}
                          {r.sharedStaff?.length > 1 && (
                            <span
                              className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-[#D9B36A] bg-[#FBF0D9] text-[#8A6A2B] font-semibold"
                              data-testid={`shared-staff-count-${r.roomId}`}
                              title={`${r.sharedStaff.length} staff sharing this room`}
                            >
                              {r.sharedStaff.length} shared
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-1 flex-wrap justify-end" data-testid={`ratio-room-badges-${r.roomId}`}>
                          {badges.map((status) => (
                            <span
                              key={status}
                              className={`flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full border ${BADGE_STYLE[status]}`}
                              data-testid={`ratio-badge-${status}-${r.roomId}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${BADGE_DOT[status]}`} />
                              {status === "full" ? "Full" : status === "under" ? "Under Ratio" : "Ratio OK"}
                            </span>
                          ))}
                        </span>
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-1 space-y-1.5">
                          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Live Room</p>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">Children</span>
                            <span className="font-semibold text-slate-700">{r.childrenPresent}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className={r.isUnderRatio ? "text-[#B85C5C]" : "text-slate-500"}>Staff</span>
                            <span className="flex items-center gap-1.5">
                              {r.poolDriven ? (
                                <span className="font-semibold text-slate-600" data-testid={`pool-driven-hint-${r.roomId}`}>
                                  {r.staffCovering} staff — see shared pool below
                                </span>
                              ) : (
                                <span className={`font-semibold ${r.isUnderRatio ? "text-[#B85C5C]" : "text-slate-700"}`}>{r.staffCovering} of {r.requiredStaff} required</span>
                              )}
                              {!r.poolDriven && !r.isUnderRatio && r.requiredStaff === 0 && (
                                <span className="text-slate-400 font-semibold" data-testid={`staffed-check-${r.roomId}`}>No staff needed</span>
                              )}
                              {!r.poolDriven && !r.isUnderRatio && r.requiredStaff > 0 && (
                                <span className="text-[#3F7A5C] font-semibold" data-testid={`staffed-check-${r.roomId}`}>✓ Staffed</span>
                              )}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">Ratio</span>
                            <span className="font-semibold text-slate-700" data-testid={`ratio-value-${r.roomId}`}>1:{r.effectiveRatio}</span>
                          </div>
                          {r.effectiveRatio !== r.licensedRatio && (
                            <p className="text-[10px] text-[#B8873C]" data-testid={`ratio-dropped-note-${r.roomId}`}>
                              Dropped from 1:{r.licensedRatio} — supervising a stricter age group here
                            </p>
                          )}
                          {r.guestChildren?.length > 0 && (
                            <p className="text-[11px] text-[#8A6A2B]" data-testid={`guest-children-${r.roomId}`}>
                              +{r.guestChildren.length} {r.guestChildren[0].nativeCategoryLabel} (Room for Ratios)
                            </p>
                          )}
                          {r.primaryStaff?.length > 0 && (
                            <p className="text-[11px] text-slate-500 truncate" data-testid={`covering-staff-${r.roomId}`}>
                              Covering: {r.primaryStaff.map((s) => s.name).join(", ")}
                            </p>
                          )}
                          {r.sharedStaff?.length > 0 && (
                            <div className="pt-1 space-y-1" data-testid={`shared-staff-list-${r.roomId}`}>
                              {r.sharedStaff.map((s) => (
                                <div
                                  key={s.staffId}
                                  className="flex items-center gap-1 text-[11px] text-[#B8873C] cursor-help"
                                  title={s.coverageType === "same_time"
                                    ? "Supervising both groups together — see the shared coverage note below."
                                    : "A different-shift assignment — this room's ratio stays independent."}
                                  data-testid={`shared-staff-${s.staffId}-${r.roomId}`}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#D8A657] shrink-0" />
                                  <span className="truncate">{s.name} — {s.coverageType === "same_time" ? "Shared Staff (Same Time)" : "Shared Staff"}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="w-px bg-slate-100" />
                        <div className="flex-1 flex flex-col items-center justify-center text-center">
                          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1 self-start">Child Slots</p>
                          <div className="relative w-16 h-16 mt-1">
                            <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                              <circle cx="18" cy="18" r="15.5" fill="none" stroke="#f1f5f9" strokeWidth="4" />
                              <circle
                                cx="18" cy="18" r="15.5" fill="none" stroke="#4C8C8C" strokeWidth="4"
                                strokeDasharray={`${(pct / 100) * 97.4} 97.4`} strokeLinecap="round"
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-xs font-bold text-slate-700">{r.childrenPresent}/{r.capacity}</span>
                            </div>
                          </div>
                          <p className="text-xs text-[#4C8C8C] font-semibold mt-1">{r.seatsAvailable} Child Slots Open</p>
                          <p className="text-[10px] text-slate-400 mt-0.5" data-testid={`staff-needed-at-capacity-${r.roomId}`}>
                            Needs {r.staffNeededAtCapacity} staff at full capacity
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {sharedPools.length > 0 && (
        <div className="mt-3 space-y-2">
          {sharedPools.map((p, idx) => (
            <div
              key={idx}
              className="flex items-start gap-2 rounded-lg border border-dashed border-[#D8A657]/50 bg-[#D8A657]/[0.06] px-3 py-2"
              data-testid={`shared-pool-note-${idx}`}
            >
              <AlertTriangle className="w-4 h-4 text-[#B8873C] shrink-0 mt-0.5" />
              <div className="text-xs text-[#8a6529]">
                <span className="font-medium">
                  {p.staff.map((s) => s.name).join(", ")} — supervising {p.categoryLabels[0]} + {p.categoryLabels[1]} together:
                </span>{" "}
                combined ratio 1:{p.combinedRatio}, {p.combinedChildren} children sharing {p.combinedAllowance} total allowance —{" "}
                <strong className={p.combinedRemaining === 0 ? "text-[#B85C5C]" : "text-[#8a6529]"}>{p.combinedRemaining} open</strong>
                {p.isUnderRatio && <span className="font-semibold"> — Under Ratio</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {unassignedChildren.length > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-dashed border-[#D8A657]/50 bg-[#D8A657]/[0.06] px-3 py-2" data-testid="unassigned-children-alert">
          <AlertTriangle className="w-4 h-4 text-[#B8873C] shrink-0 mt-0.5" />
          <div className="text-xs text-[#8a6529]">
            <span className="font-medium">Unassigned children present:</span>{" "}
            {unassignedChildren.map((c) => c.childName).join(", ")} — their category has no room configured yet. Add a room for that category in Settings so they count toward its ratio.
          </div>
        </div>
      )}

      {staffMissingRoomAssignment.length > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-dashed border-[#D8A657]/50 bg-[#D8A657]/[0.06] px-3 py-2" data-testid="staff-missing-room-alert">
          <AlertTriangle className="w-4 h-4 text-[#B8873C] shrink-0 mt-0.5" />
          <div className="text-xs text-[#8a6529]">
            <span className="font-medium">Staff needing a room assignment:</span>{" "}
            {staffMissingRoomAssignment.map((s) => `${s.name} (${s.category})`).join(", ")} — this category has more than one room; pick a specific room in Staff Records so they count toward its ratio.
          </div>
        </div>
      )}
    </div>
  );
};
